import React, { useId, useLayoutEffect, useRef, useState } from 'react';
import {
  ICreature,
  StatusEffect,
  StudyLevel,
  isDefFixedBuff,
  isDefeated,
  isInHpCrisis,
  isMDefFixedBuff,
} from '@common/FabulaUltimaGame';
import { formatFabulaUltimaCommandArgument, formatFabulaUltimaCommandForDisplay } from '@common/FabulaUltimaCommand';
import { CreatureAvatar } from './CreatureAvatar';

import zenitIcon from '../../assets/Zenit.png';
import dazedIcon from '../../assets/status-effects/dazed.png';
import enragedIcon from '../../assets/status-effects/enraged.png';
import poisonedIcon from '../../assets/status-effects/poisoned.png';
import shakenIcon from '../../assets/status-effects/shaken.png';
import slowIcon from '../../assets/status-effects/slow.png';
import weakIcon from '../../assets/status-effects/weak.png';
import { statusEffectColors } from '../../assets/status-effects/generatedColors';
import {
  formatFuBuff,
  fuText,
  getFuBuffTemplates,
  resolveFuBuffInput,
  statusLabels,
  type FuLocale,
} from '../../i18n/fuLocale';
import {
  routeStatusConnector,
  type StatusConnectorRoute,
} from './statusConnectorGeometry';
import './CreatureCard.css';

type AttributeId = 'dex' | 'ins' | 'mig' | 'wlp';

type StatusDefinition = {
  effect: StatusEffect;
  icon: string;
  targets: AttributeId[];
};

const STATUS_DEFINITIONS: StatusDefinition[] = [
  { effect: 'slow', icon: slowIcon, targets: ['dex'] },
  { effect: 'dazed', icon: dazedIcon, targets: ['ins'] },
  { effect: 'enraged', icon: enragedIcon, targets: ['dex', 'ins'] },
  { effect: 'weak', icon: weakIcon, targets: ['mig'] },
  { effect: 'shaken', icon: shakenIcon, targets: ['wlp'] },
  { effect: 'poisoned', icon: poisonedIcon, targets: ['mig', 'wlp'] },
];

const ATTRIBUTE_LABELS = {
  zh: { dex: '敏捷', ins: '洞察', mig: '力量', wlp: '意志', def: '物防', mdef: '魔防' },
  en: { dex: 'DEX', ins: 'INS', mig: 'MIG', wlp: 'WLP', def: 'DEF', mdef: 'MDEF' },
} as const;

function formatStudyLevel(level: StudyLevel, locale: FuLocale): string {
  if (level === 'null') return fuText(locale, 'notStudied');
  return locale === 'zh' ? `研究 ${level}` : `Study ${level}`;
}

function studyLevelClass(level: StudyLevel): string {
  if (level === '7+') return 'study-level--partial';
  if (level === '10+' || level === '13+') return 'study-level--complete';
  return 'study-level--none';
}

type StatBarProps = {
  current: number;
  max: number;
  type: 'hp' | 'mp';
  hidden?: boolean;
};

const StatBar: React.FC<StatBarProps> = ({ current, max, type, hidden = false }) => (
  <div className="stat-bar-container">
    <div
      className={`stat-bar ${type}-bar`}
      style={{
        width: `${hidden ? 100 : (current / max) * 100}%`,
        backgroundColor: hidden ? '#6c757d' : undefined,
      }}
    />
    <span className="stat-text">
      {hidden ? `MAX - ${max - current}` : `${current}/${max}`}
    </span>
  </div>
);

const IpDisplay: React.FC<{ current: number; max: number }> = ({ current, max }) => (
  <div className="ip-display">
    {Array.from({ length: max }, (_, index) => (
      <img
        key={index}
        src={zenitIcon}
        alt={index < current ? 'IP' : 'IP (used)'}
        className={`ip-coin${index >= current ? ' ip-coin-used' : ''}`}
      />
    ))}
  </div>
);

interface CreatureCardProps {
  creature: ICreature;
  isHost: boolean;
  canEdit?: boolean;
  onRunCommands?: (commands: string[]) => void;
  buffHistory?: string[];
  locale?: FuLocale;
}

export const CreatureCard: React.FC<CreatureCardProps> = ({
  creature,
  isHost,
  canEdit = false,
  onRunCommands,
  buffHistory = [],
  locale = 'zh',
}) => {
  const buffInputId = useId();
  const buffInputRef = useRef<HTMLInputElement>(null);
  const connectorLayerRef = useRef<SVGSVGElement>(null);
  const statusPanelRef = useRef<HTMLElement>(null);
  const statusGridRef = useRef<HTMLDivElement>(null);
  const statGridRef = useRef<HTMLDivElement>(null);
  const statusButtonRefs = useRef(new Map<StatusEffect, HTMLButtonElement>());
  const attributeRefs = useRef(new Map<AttributeId, HTMLDivElement>());
  const lastSentAt = useRef(new Map<string, number>());
  const [hoveredStatus, setHoveredStatus] = useState<StatusEffect | null>(null);
  const [connectorRoute, setConnectorRoute] = useState<StatusConnectorRoute | null>(null);
  const [buffInput, setBuffInput] = useState('');
  const [showBuffSuggestions, setShowBuffSuggestions] = useState(false);
  const [showBuffSuggestionsAbove, setShowBuffSuggestionsAbove] = useState(false);
  const [buffSuggestionsMaxHeight, setBuffSuggestionsMaxHeight] = useState(240);
  const [feedback, setFeedback] = useState('');

  const hidesEnemyResources = !isHost
    && creature.kind === 'enemy'
    && creature.studyLevel === 'null';
  const hidesEnemyAttributes = !isHost
    && creature.kind === 'enemy'
    && (creature.studyLevel === 'null' || creature.studyLevel === '7+');
  const lockMessage = isHost
    ? fuText(locale, 'historyLockedGm')
    : fuText(locale, 'historyLockedPlayer');
  const resolvedBuff = resolveFuBuffInput(buffInput, locale);
  const isBuffInputValid = resolvedBuff !== null
    && !creature.buffs.includes(resolvedBuff);
  const buffTemplateSuggestions = getFuBuffTemplates(locale).filter((template) => {
    const query = buffInput.trim().toLocaleLowerCase();
    return !query
      || template.inputPrefix.toLocaleLowerCase().includes(query)
      || template.protocolPrefix.toLocaleLowerCase().includes(query);
  });
  // Free-text buff names used before anywhere in this room (see the Buff/debuff
  // entry in docs/contexts/fabula-ultima/CONTEXT.md) — kept separate from the
  // mechanical templates above since they carry no game-mechanical meaning.
  const buffHistorySuggestions = buffHistory.filter((name) => {
    const query = buffInput.trim().toLocaleLowerCase();
    return !creature.buffs.includes(name)
      && (!query || name.toLocaleLowerCase().includes(query));
  });
  const hoveredDefinition = STATUS_DEFINITIONS.find(
    ({ effect }) => effect === hoveredStatus,
  );
  const attributeLabels = ATTRIBUTE_LABELS[locale];

  useLayoutEffect(() => {
    const input = buffInputRef.current;
    if (!showBuffSuggestions || !input) return;
    const scrollport = input.closest('.fu-battlefield-scroll');
    const updatePlacement = () => {
      const inputBounds = input.getBoundingClientRect();
      const scrollBounds = scrollport?.getBoundingClientRect();
      const top = Math.max(0, scrollBounds?.top ?? 0);
      const bottom = Math.min(window.innerHeight, scrollBounds?.bottom ?? window.innerHeight);
      const above = Math.max(0, inputBounds.top - top - 8);
      const below = Math.max(0, bottom - inputBounds.bottom - 8);
      const desiredHeight = Math.min(240, (buffTemplateSuggestions.length + buffHistorySuggestions.length) * 34 + 80);
      const opensAbove = below < desiredHeight && above > below;
      setShowBuffSuggestionsAbove(opensAbove);
      setBuffSuggestionsMaxHeight(inputBounds.bottom <= top || inputBounds.top >= bottom
        ? 0 : Math.min(240, opensAbove ? above : below));
    };
    updatePlacement();
    scrollport?.addEventListener('scroll', updatePlacement, { passive: true });
    window.addEventListener('resize', updatePlacement);
    return () => {
      scrollport?.removeEventListener('scroll', updatePlacement);
      window.removeEventListener('resize', updatePlacement);
    };
  }, [showBuffSuggestions, buffTemplateSuggestions.length, buffHistorySuggestions.length]);

  useLayoutEffect(() => {
    if (!hoveredDefinition || hidesEnemyAttributes) {
      setConnectorRoute(null);
      return;
    }

    const connectorLayer = connectorLayerRef.current;
    const statusPanel = statusPanelRef.current;
    const statusGrid = statusGridRef.current;
    const statGrid = statGridRef.current;
    const statusButton = statusButtonRefs.current.get(hoveredDefinition.effect);
    const targetElements = hoveredDefinition.targets
      .map((target) => attributeRefs.current.get(target))
      .filter((target): target is HTMLDivElement => Boolean(target));
    if (!connectorLayer || !statusPanel || !statusGrid || !statGrid || !statusButton
      || targetElements.length === 0) {
      setConnectorRoute(null);
      return;
    }

    const updateRoute = () => {
      const layerBounds = connectorLayer.getBoundingClientRect();
      const buttonBounds = statusButton.getBoundingClientRect();
      const panelBounds = statusPanel.getBoundingClientRect();
      const statusGridBounds = statusGrid.getBoundingClientRect();
      const statGridBounds = statGrid.getBoundingClientRect();
      setConnectorRoute(routeStatusConnector({
        statusAnchor: {
          x: buttonBounds.left + buttonBounds.width / 2 - layerBounds.left,
          y: buttonBounds.top - layerBounds.top,
        },
        statBounds: targetElements.map((element) => {
          const bounds = element.getBoundingClientRect();
          return {
            left: bounds.left - layerBounds.left,
            right: bounds.right - layerBounds.left,
            top: bounds.top - layerBounds.top,
          };
        }),
        statusLaneY: statusGridBounds.top - layerBounds.top - 6,
        dividerX: panelBounds.left - layerBounds.left,
        statLaneY: statGridBounds.top - layerBounds.top - 6,
      }));
    };

    updateRoute();
    const resizeObserver = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(updateRoute);
    [connectorLayer, statusPanel, statusGrid, statGrid, statusButton, ...targetElements]
      .forEach((element) => resizeObserver?.observe(element));
    window.addEventListener('resize', updateRoute);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateRoute);
    };
  }, [hidesEnemyAttributes, hoveredDefinition]);

  const sendCommand = (controlId: string, command: string, cooldownMs = 400): boolean => {
    const now = Date.now();
    const previous = lastSentAt.current.get(controlId) ?? 0;
    if (!canEdit || !onRunCommands || now - previous < cooldownMs) return false;

    lastSentAt.current.set(controlId, now);
    onRunCommands([command]);
    setFeedback(formatFabulaUltimaCommandForDisplay(command));
    window.setTimeout(() => setFeedback(''), 1500);
    return true;
  };

  const addBuff = () => {
    if (!isBuffInputValid) return;
    const creatureName = formatFabulaUltimaCommandArgument(creature.name);
    const buffName = formatFabulaUltimaCommandArgument(resolvedBuff!);
    if (sendCommand('buff:add', `buff ${creatureName} +${buffName}`)) {
      setBuffInput('');
    }
  };

  const renderAttribute = (
    id: AttributeId | 'def' | 'mdef',
    label: string,
    value: number,
    debuffed = false,
  ) => {
    const isLinked = hoveredDefinition?.targets.includes(id as AttributeId)
      && !hidesEnemyAttributes;
    return (
      <div
        className={`basic-stat${isLinked ? ' linked-stat' : ''}`}
        data-stat={id}
        ref={(element) => {
          if (id === 'def' || id === 'mdef') return;
          if (element) attributeRefs.current.set(id, element);
          else attributeRefs.current.delete(id);
        }}
      >
        <label>{label}</label>
        <span className={debuffed ? 'debuffed' : ''}>
          {hidesEnemyAttributes ? '???' : value}
        </span>
      </div>
    );
  };

  return (
    <div className={`creature-card${isDefeated(creature.hp) ? ' hp-defeated' : isInHpCrisis(creature.hp, creature.maxHp) ? ' hp-crisis' : ''}`}>
      {!hidesEnemyAttributes && (
        <svg ref={connectorLayerRef} className="status-links" aria-hidden="true">
          {connectorRoute?.path && (
            <path
              className="status-link"
              d={connectorRoute.path}
              vectorEffect="non-scaling-stroke"
            />
          )}
        </svg>
      )}

      <section className="creature-main">
        <div className="creature-name-row">
          <CreatureAvatar name={creature.name} avatar={creature.avatar} locale={locale} identity={creature.identity ?? creature}
            canEdit={canEdit && Boolean(onRunCommands)} onChange={avatar => {
              const name = formatFabulaUltimaCommandArgument(creature.name);
              sendCommand('creature:avatar', `avatar ${name} ${avatar || 'clear'}`, 0);
            }} />
          <div className="creature-heading">
            <h3 className="creature-name">{creature.name}</h3>
            {creature.kind === 'enemy' && (
              <span className={`study-level ${studyLevelClass(creature.studyLevel)}`}>
                🔎 {formatStudyLevel(creature.studyLevel, locale)}
              </span>
            )}
          </div>
          {isHost && (
            <button
              type="button"
              className="creature-delete-button"
              disabled={!canEdit}
              title={!canEdit ? lockMessage : fuText(locale, 'deleteCreature')}
              aria-label={fuText(locale, 'deleteCreature')}
              onClick={() => {
                if (window.confirm(`确认删除 ${creature.name}？此操作不可撤销。`)) {
                  const name = formatFabulaUltimaCommandArgument(creature.name);
                  sendCommand('creature:remove', `removeCreature ${name}`);
                }
              }}
            >
              🗑
            </button>
          )}
        </div>
        <div className="stats-container">
          <div className="stat-group stat-group--hp">
            <label>HP</label>
            <StatBar current={creature.hp} max={creature.maxHp} type="hp" hidden={hidesEnemyResources} />
          </div>
          <div className="stat-group stat-group--mp">
            <label>MP</label>
            <StatBar current={creature.mp} max={creature.maxMp} type="mp" hidden={hidesEnemyResources} />
          </div>
          {creature.kind === 'ally' && creature.maxIp > 0 && (
            <div className="stat-group">
              <label>IP</label>
              <IpDisplay current={creature.ip} max={creature.maxIp} />
            </div>
          )}
          <div ref={statGridRef} className="basic-stats">
            {renderAttribute('dex', attributeLabels.dex, creature.currentDex, creature.currentDex < creature.dex)}
            {renderAttribute('ins', attributeLabels.ins, creature.currentIns, creature.currentIns < creature.ins)}
            {renderAttribute('mig', attributeLabels.mig, creature.currentMig, creature.currentMig < creature.mig)}
            {renderAttribute('wlp', attributeLabels.wlp, creature.currentWlp, creature.currentWlp < creature.wlp)}
            {renderAttribute('def', `${attributeLabels.def}${creature.fixedPhysicalDefense || creature.buffs.some(isDefFixedBuff) ? '★' : ''}`, creature.physicalDefense, !creature.fixedPhysicalDefense && !creature.buffs.some(isDefFixedBuff) && creature.currentDex < creature.dex)}
            {renderAttribute('mdef', `${attributeLabels.mdef}${creature.buffs.some(isMDefFixedBuff) ? '★' : ''}`, creature.magicDefense, !creature.buffs.some(isMDefFixedBuff) && creature.currentIns < creature.ins)}
          </div>
        </div>
      </section>

      <aside ref={statusPanelRef} className="creature-status-panel" aria-label={fuText(locale, 'statusPanel')}>
        <div className="status-panel-heading">
          <h4>{fuText(locale, 'statusPanel')}</h4>
        </div>

        <div ref={statusGridRef} className="effect-grid">
          {STATUS_DEFINITIONS.map(({ effect, icon }) => {
            const isActive = creature.statusEffects.includes(effect);
            return (
              <button
                key={effect}
                ref={(element) => {
                  if (element) statusButtonRefs.current.set(effect, element);
                  else statusButtonRefs.current.delete(effect);
                }}
                type="button"
                className={`effect-control${isActive ? ' active' : ''}`}
                style={{ '--status-light-color': statusEffectColors[effect].light } as React.CSSProperties}
                disabled={!canEdit}
                title={!canEdit ? lockMessage : statusLabels[locale][effect]}
                onMouseEnter={() => setHoveredStatus(effect)}
                onMouseLeave={() => setHoveredStatus(null)}
                onFocus={() => setHoveredStatus(effect)}
                onBlur={() => setHoveredStatus(null)}
                onClick={() => {
                  const name = formatFabulaUltimaCommandArgument(creature.name);
                  sendCommand(`status:${effect}`, `status ${name} ${isActive ? '-' : '+'}${effect}`);
                }}
              >
                <img src={icon} alt="" />
                <span>{statusLabels[locale][effect]}</span>
              </button>
            );
          })}
        </div>

        <div className="buff-editor">
          <label htmlFor={buffInputId}>{fuText(locale, 'buffs')}</label>
          <div className="buff-chips">
            {creature.buffs.map((buff) => {
              const presentation = formatFuBuff(buff, locale);
              return (
              <span
                className={`buff-chip${presentation.isMechanical ? ' buff-chip--mechanical' : ''}`}
                key={buff}
                title={presentation.isMechanical ? (locale === 'zh' ? '具有游戏机制效果' : 'Mechanical effect') : undefined}
              >
                {presentation.isMechanical && <span aria-hidden="true">⚙ </span>}
                {presentation.label}
                <button
                  type="button"
                  disabled={!canEdit}
                  aria-label={`${locale === 'zh' ? '移除' : 'Remove'} ${buff}`}
                  onClick={() => {
                    const name = formatFabulaUltimaCommandArgument(creature.name);
                    const buffName = formatFabulaUltimaCommandArgument(buff);
                    sendCommand(`buff:${buff}`, `buff ${name} -${buffName}`);
                  }}
                >
                  ×
                </button>
              </span>
              );
            })}
          </div>
          <div className="buff-add">
            <input
              ref={buffInputRef}
              id={buffInputId}
              value={buffInput}
              disabled={!canEdit}
              onChange={(event) => setBuffInput(event.target.value)}
              onFocus={() => setShowBuffSuggestions(true)}
              onBlur={() => window.setTimeout(() => setShowBuffSuggestions(false), 100)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') addBuff();
              }}
            />
            <button
              type="button"
              aria-label={fuText(locale, 'add')}
              title={fuText(locale, 'add')}
              disabled={!canEdit || !isBuffInputValid}
              onClick={addBuff}
            >
              ＋
            </button>
            {showBuffSuggestions && (buffTemplateSuggestions.length > 0 || buffHistorySuggestions.length > 0) && (
              <div
                className={`buff-suggestions${showBuffSuggestionsAbove ? ' buff-suggestions--above' : ''}`}
                style={{ maxHeight: buffSuggestionsMaxHeight, overflowY: 'auto' }}
                role="listbox"
              >
                {buffTemplateSuggestions.map((template) => (
                  <button
                    key={template.protocolPrefix}
                    type="button"
                    role="option"
                    aria-selected="false"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      setBuffInput(`${template.inputPrefix} `);
                      setShowBuffSuggestions(false);
                      buffInputRef.current?.focus();
                    }}
                  >
                    <span aria-hidden="true">⚙</span> {template.suggestion}
                  </button>
                ))}
                {buffHistorySuggestions.length > 0 && (
                  <>
                    <div className="buff-suggestions-heading">{fuText(locale, 'buffHistoryHeading')}</div>
                    {buffHistorySuggestions.map((name) => (
                      <button
                        key={name}
                        type="button"
                        role="option"
                        aria-selected="false"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => {
                          setBuffInput(name);
                          setShowBuffSuggestions(false);
                          buffInputRef.current?.focus();
                        }}
                      >
                        {name}
                      </button>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {!canEdit && <p className="edit-lock-message">{lockMessage}</p>}
        {feedback && <p className="command-feedback">{fuText(locale, 'sent')}{feedback}</p>}
      </aside>
    </div>
  );
};
